import {
  airworthinessDirectiveGroupPages,
  airworthinessDirectivesInfiniteQuery,
} from "@common/api/documents"
import { ensureListData } from "@common/api/utils"
import { Button } from "@common/components/ui/button"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { DirectiveTable } from "./-components/directive-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
} from "./-lib/columns"
import {
  directiveFilterConfigs,
  hasDirectiveFilters,
  NO_DIRECTIVE_FILTERS,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS } from "./-lib/grouping"
import {
  type DirectivesDisplay,
  type DirectivesSearch,
  directivePagesParams,
  directivesSearchSchema,
  displayOf,
  feedOf,
  TABS,
  tabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/airworthiness-directives")({
  staticData: { breadcrumb: "Airworthiness Directives" },
  validateSearch: (search) => directivesSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    pages: directivePagesParams(search),
    grouped: Boolean(displayOf(search).group && !search.search),
  }),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "document:read", "/")
  },
  loader: ({ context: { queryClient }, deps }) =>
    ensureListData({
      grouped: deps.grouped,
      groups: () =>
        queryClient.ensureQueryData(
          airworthinessDirectiveGroupPages.groups(deps.pages),
        ),
      flat: () =>
        queryClient.ensureInfiniteQueryData(
          airworthinessDirectivesInfiniteQuery(deps.pages),
        ),
    }),
  component: DirectivesPage,
})

function useUpdateSearch(): (patch: Partial<DirectivesSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function DirectivesPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)

  return (
    <ListPage
      toolbar={<Toolbar display={display} hidden={fit.hidden} />}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilters />
      <DirectiveTable
        search={search}
        display={display}
        columns={fit.shown}
        onSort={(column) => update(sortSearch(display, column))}
        onClear={() => update({ ...NO_DIRECTIVE_FILTERS, search: undefined })}
      />
    </ListPage>
  )
}

function Toolbar({
  display,
  hidden,
}: {
  display: DirectivesDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const { tab = "current", search = "" } = values
  const update = useUpdateSearch()

  return (
    <ListToolbar
      tabs={
        <ListTabs
          tabs={TABS}
          tab={tab}
          label={tabLabel}
          onSelect={(next) =>
            update({
              tab: next === "current" ? undefined : next,
              source_feed: feedOf(next) ? undefined : values.source_feed,
            })
          }
        />
      }
      actions={
        <>
          <NameFilter
            search={search}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search directives…"
          />
          <FilterButton
            active={hasDirectiveFilters(values)}
            configs={directiveFilterConfigs(tab)}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={({ group, subgroup }) => update({ group, subgroup })}
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={["number"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          <UploadButton />
        </>
      }
    />
  )
}

function sortSearch(
  display: DirectivesDisplay,
  column: Column,
): Partial<DirectivesSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "added" && direction === "desc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function columnsSearch(columns: Column[]): Partial<DirectivesSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function UploadButton(): React.ReactElement | null {
  const canUpload = useHasPermission("document:create")
  if (!canUpload) return null

  return (
    <Button size="sm" asChild>
      <Link to="/airworthiness-directives/upload" search={true}>
        Upload AD
      </Link>
    </Button>
  )
}

function ActiveFilters(): React.ReactElement {
  const { tab = "current", ...values } = Route.useSearch()
  const update = useUpdateSearch()

  return (
    <ActiveFilterBar
      active={hasDirectiveFilters(values)}
      configs={directiveFilterConfigs(tab)}
      values={values}
      onChange={update}
      onClear={() => update(NO_DIRECTIVE_FILTERS)}
    />
  )
}
